import React, { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { useDropzone } from 'react-dropzone';
import { X, Upload, File as FileIcon, Eye } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/utils';
import { useUploadBinaryMutation, useAssignFolderMutation } from '@/store/api/uploadApi';

export interface UploadedFileItem {
  id: string;
  file: File;
  preview?: string;
  filename?: string;
  url?: string;
  progress?: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
}

const FOLDERS = ['tech', 'nontech', 'semitech'] as const;

export default function FileInput({
  onChange,
  className,
  multiple = true,
}: {
  onChange?: (files: UploadedFileItem[]) => void;
  className?: string;
  multiple?: boolean;
}) {
  const [files, setFiles] = useState<UploadedFileItem[]>([]);
  const [folder, setFolder] = useState('');

  const [uploadBinary] = useUploadBinaryMutation();
  const [assignFolder] = useAssignFolderMutation();

  //  Upload Flow
  const uploadFileFlow = async (item: UploadedFileItem) => {
    if (!folder) return;

    // set uploading state
    setFiles((prev) =>
      prev.map((f) => (f.id === item.id ? { ...f, status: 'uploading', progress: 20 } : f))
    );

    try {
      const uploadRes = await uploadBinary(item.file).unwrap();

      // mid progress
      setFiles((prev) => prev.map((f) => (f.id === item.id ? { ...f, progress: 70 } : f)));

      const assignRes = await assignFolder({
        folder,
        filename: uploadRes.meta.filename,
      }).unwrap();

      // completed
      setFiles((prev: any) => {
        const updated = prev.map((f: any) =>
          f.id === item.id
            ? {
                ...f,
                status: 'completed',
                progress: 100,
                filename: uploadRes.meta.filename,
                url: assignRes.url,
              }
            : f
        );

        onChange?.(updated);
        return updated;
      });

      toast.success('File uploaded successfully ');
    } catch {
      setFiles((prev) => prev.map((f) => (f.id === item.id ? { ...f, status: 'error' } : f)));

      toast.error('Upload failed ');
    }
  };

  //  DROP (PREVIEW ONLY)
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (!folder) {
        toast.error('Select folder first');
        return;
      }

      const newFiles: UploadedFileItem[] = acceptedFiles.map((file) => ({
        id: crypto.randomUUID(),
        file,
        preview: URL.createObjectURL(file),
        progress: 0,
        status: 'pending',
      }));

      setFiles((prev) => [...prev, ...newFiles]);
    },
    [folder]
  );

  const removeFile = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const uploadAll = async () => {
    for (const item of files) {
      if (item.status === 'pending') {
        await uploadFileFlow(item);
      }
    }

    //  clear all after upload
    setTimeout(() => {
      setFiles([]);
    }, 500); // small delay for UX
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple,
    disabled: !folder,
  });
  const handlePreview = (item: UploadedFileItem) => {
    if (item.url) {
      // after upload (server URL)
      window.open(item.url, '_blank');
    } else if (item.preview) {
      // before upload (local file)
      window.open(item.preview, '_blank');
    } else {
      toast.error('Preview not available');
    }
  };
  return (
    <div className={cn('space-y-4', className)}>
      <select
        value={folder}
        onChange={(e) => setFolder(e.target.value)}
        className="w-full border rounded-md px-3 py-2 text-sm"
      >
        <option value="">Select Category</option>
        {FOLDERS.map((f) => (
          <option key={f} value={f}>
            {f.toUpperCase()}
          </option>
        ))}
      </select>

      <div
        {...getRootProps()}
        className={cn(
          'flex items-center justify-center gap-2 border border-dashed rounded-lg p-5',
          folder
            ? 'cursor-pointer hover:border-muted-foreground/60'
            : 'opacity-50 cursor-not-allowed',
          isDragActive && 'border-primary bg-primary/5'
        )}
      >
        <input {...getInputProps()} />
        <Upload className="h-5 w-5" />
        <span>{folder ? 'Drag & drop or click' : 'Select Category first'}</span>
      </div>

      {files.map((item) => (
        <div key={item.id} className="flex items-center gap-3 bg-muted p-3 rounded-md">
          {item.file.type.startsWith('image/') ? (
            <img src={item.preview} className="h-12 w-12 object-cover rounded" />
          ) : (
            <FileIcon className="h-6 w-6" />
          )}

          <div className="flex-1">
            <p className="text-sm truncate">{item.file.name}</p>

            {item.status === 'uploading' && <Progress value={item.progress} className="h-1 mt-1" />}

            {item.status === 'completed' && <p className="text-xs text-green-600">Uploaded ✅</p>}

            {item.status === 'error' && <p className="text-xs text-red-600">Failed ❌</p>}
          </div>
          <Button variant="ghost" size="icon" onClick={() => handlePreview(item)}>
            <Eye className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => removeFile(item.id)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}

      {files.length > 0 && (
        <Button onClick={uploadAll} className="w-full">
          Upload Files
        </Button>
      )}
    </div>
  );
}
